import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, MessageCircle } from "lucide-react";
import { useSite } from "@/i18n/SiteContext";
import { api, waLink } from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";

const trPost = (post, lang) => {
  const tr = post?.translations || {};
  return { ...(tr.en || {}), ...(tr[lang] || {}) };
};

export default function DiaryPostPage() {
  const { slug } = useParams();
  const { t, lang, settings } = useSite();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    api.get(`/diary/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    api.get("/diary", { params: { limit: 6 } }).then((r) => setRelated((r.data || []).filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-6 bg-ivory" data-testid="diary-not-found">
        <h1 className="font-serif text-3xl text-ocean">{t("diary.empty")}</h1>
        <Link to="/diary" className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-ocean hover:text-sea">
          <ArrowLeft size={14} /> {t("diary.backToDiary")}
        </Link>
      </div>
    );
  }

  const tr = post ? trPost(post, lang) : {};

  return (
    <div className="bg-ivory" data-testid="diary-post-page">
      {/* HERO */}
      <section className="relative h-[60vh] min-h-[440px] flex items-end overflow-hidden" data-testid="diary-post-hero">
        {post ? (
          <img src={post.image} alt={tr.title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-sand/60 animate-pulse" />
        )}
        <div className="hero-overlay absolute inset-0" />
        <Reveal className="relative max-w-4xl mx-auto w-full px-6 pb-14">
          <Link to="/diary" className="inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-ivory/80 hover:text-sand-brand mb-6" data-testid="diary-post-back">
            <ArrowLeft size={14} /> {t("diary.backToDiary")}
          </Link>
          {post?.tags?.length ? (
            <p className="text-[11px] font-medium tracking-[0.3em] text-sand-brand uppercase mb-4">{post.tags[0]}</p>
          ) : null}
          <h1 className="font-serif text-4xl md:text-6xl text-ivory font-normal leading-[1.05]" data-testid="diary-post-title">{tr.title || "—"}</h1>
          {tr.excerpt && <p className="mt-5 text-base md:text-lg text-ivory/85 max-w-2xl leading-relaxed italic">“{tr.excerpt}”</p>}
        </Reveal>
      </section>

      {/* BODY */}
      <section className="px-6 py-20 md:py-24">
        <div className="max-w-3xl mx-auto">
          {post ? (
            <article
              className="prose prose-stone max-w-none font-serif text-lg md:text-xl text-ocean/85 leading-[1.75] [&_p]:mb-6 [&_strong]:text-ocean [&_em]:text-sea"
              data-testid="diary-post-body"
              dangerouslySetInnerHTML={{ __html: tr.content || "" }}
            />
          ) : (
            <div className="space-y-4" data-testid="diary-post-loading">
              {[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-5 rounded bg-sand/50 animate-pulse" style={{ width: `${80 - i * 6}%` }} />)}
            </div>
          )}

          {/* CTAs */}
          <div className="mt-14 pt-10 border-t border-sand flex flex-col sm:flex-row gap-4" data-testid="diary-post-ctas">
            <Link to="/tours" className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full bg-ocean text-ivory text-xs font-medium tracking-[0.25em] uppercase btn-lift" data-testid="diary-cta-tours">
              {t("diary.exploreTours")} <ArrowRight size={14} />
            </Link>
            {settings?.whatsapp && (
              <a
                href={waLink(settings.whatsapp, t("wa.default"))}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-ocean/20 text-ocean text-xs font-medium tracking-[0.25em] uppercase hover:bg-ocean hover:text-ivory btn-lift"
                data-testid="diary-cta-whatsapp"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="px-6 pb-24">
          <div className="max-w-6xl mx-auto">
            <p className="text-[11px] tracking-[0.3em] uppercase text-sea mb-6">{t("diary.related")}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((p) => {
                const rtr = trPost(p, lang);
                return (
                  <Link
                    key={p.id || p.slug}
                    to={`/diary/${p.slug}`}
                    className="group block bg-white rounded-2xl overflow-hidden border border-sand/60 hover:shadow-[0_12px_30px_rgba(15,46,62,0.10)] transition-shadow"
                    data-testid={`diary-related-${p.slug}`}
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={p.image} alt={rtr.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="p-5">
                      <h3 className="font-serif text-lg text-ocean leading-snug">{rtr.title}</h3>
                      <p className="mt-2 text-xs text-ocean/65 line-clamp-2">{rtr.excerpt}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
